import React, { useState } from 'react';
import { MainLayout } from '@/components/layouts/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Shield, Activity, Search, Download, Filter } from 'lucide-react';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface SecurityEvent {
  id: string;
  event_type: string;
  event_data: any;
  user_id: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
  user?: {
    full_name: string;
    email: string;
  };
}

interface ActivityLog {
  id: string;
  action: string;
  entity_type: string;
  entity_name: string;
  details: any;
  user_id: string;
  company_id: string;
  created_at: string;
  user?: {
    full_name: string;
    email: string;
  };
}

export const AuditLogs: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'security' | 'activity'>('security');
  const [searchTerm, setSearchTerm] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [actionFilter, setActionFilter] = useState<string>('all');

  // Fetch statistics
  const { data: stats } = useQuery({
    queryKey: ['audit-stats'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('admin-audit-logs', {
        body: { action: 'get_stats' }
      });
      
      if (error) throw error;
      return data.data;
    }
  });

  // Fetch security events
  const { data: securityEvents, isLoading: loadingSecurityEvents } = useQuery({
    queryKey: ['security-events', eventTypeFilter],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('admin-audit-logs', {
        body: { 
          action: 'get_security_events',
          limit: 100,
          offset: 0,
          ...(eventTypeFilter !== 'all' && { eventType: eventTypeFilter })
        }
      });
      
      if (error) throw error;
      return data.data as SecurityEvent[];
    }
  });

  // Fetch activity logs
  const { data: activityLogs, isLoading: loadingActivityLogs } = useQuery({
    queryKey: ['activity-logs', actionFilter],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('admin-audit-logs', {
        body: { 
          action: 'get_activity_logs',
          limit: 100,
          offset: 0,
          ...(actionFilter !== 'all' && { action: actionFilter })
        }
      });
      
      if (error) throw error;
      return data.data as ActivityLog[];
    }
  });

  // Fetch event types for filter
  const { data: eventTypes } = useQuery({
    queryKey: ['event-types'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('admin-audit-logs', {
        body: { action: 'get_event_types' }
      });
      
      if (error) throw error;
      return data.data as string[];
    }
  });

  const getEventBadgeVariant = (eventType: string) => {
    if (eventType.includes('unauthorized') || eventType.includes('failed')) {
      return 'destructive';
    }
    if (eventType.includes('financial') || eventType.includes('sensitive')) {
      return 'default';
    }
    return 'secondary';
  };

  const filteredSecurityEvents = securityEvents?.filter(event => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      event.event_type.toLowerCase().includes(searchLower) ||
      event.user?.full_name?.toLowerCase().includes(searchLower) ||
      event.user?.email?.toLowerCase().includes(searchLower)
    );
  });

  const filteredActivityLogs = activityLogs?.filter(log => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      log.action.toLowerCase().includes(searchLower) ||
      log.entity_name.toLowerCase().includes(searchLower) ||
      log.user?.full_name?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <MainLayout 
      title="Journaux d'Audit"
      actions={
        <Button variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          Exporter
        </Button>
      }
    >
      <div className="space-y-6">
        {/* Statistics Cards */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Événements de Sécurité (7j)
              </CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalSecurityEvents || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Tous les événements de sécurité
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Activités (7j)
              </CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalActivityLogs || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Toutes les actions utilisateurs
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Card>
          <CardHeader>
            <CardTitle>Historique d'Audit</CardTitle>
            <CardDescription>
              Consultez tous les événements de sécurité et les activités des utilisateurs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
              <TabsList className="mb-4">
                <TabsTrigger value="security">
                  <Shield className="h-4 w-4 mr-2" />
                  Événements de Sécurité
                </TabsTrigger>
                <TabsTrigger value="activity">
                  <Activity className="h-4 w-4 mr-2" />
                  Activités
                </TabsTrigger>
              </TabsList>

              {/* Filters */}
              <div className="flex gap-4 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                {activeTab === 'security' ? (
                  <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
                    <SelectTrigger className="w-[200px]">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Type d'événement" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      {eventTypes?.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Select value={actionFilter} onValueChange={setActionFilter}>
                    <SelectTrigger className="w-[200px]">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Type d'action" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les actions</SelectItem>
                      {Object.keys(stats?.activityLogsByAction || {}).map(action => (
                        <SelectItem key={action} value={action}>{action}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <TabsContent value="security" className="space-y-4">
                {loadingSecurityEvents ? (
                  <LoadingSpinner />
                ) : filteredSecurityEvents && filteredSecurityEvents.length > 0 ? (
                  <div className="space-y-2">
                    {filteredSecurityEvents.map((event) => (
                      <div key={event.id} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1 flex-1">
                            <div className="flex items-center gap-2">
                              <Badge variant={getEventBadgeVariant(event.event_type)}>
                                {event.event_type}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                {format(new Date(event.created_at), 'PPp', { locale: fr })}
                              </span>
                            </div>
                            {event.user && (
                              <p className="text-sm">
                                <span className="font-medium">{event.user.full_name}</span>
                                <span className="text-muted-foreground"> ({event.user.email})</span>
                              </p>
                            )}
                            {event.event_data && (
                              <details className="text-sm text-muted-foreground">
                                <summary className="cursor-pointer hover:text-foreground">Détails</summary>
                                <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                                  {JSON.stringify(event.event_data, null, 2)}
                                </pre>
                              </details>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Aucun événement de sécurité trouvé
                  </p>
                )}
              </TabsContent>

              <TabsContent value="activity" className="space-y-4">
                {loadingActivityLogs ? (
                  <LoadingSpinner />
                ) : filteredActivityLogs && filteredActivityLogs.length > 0 ? (
                  <div className="space-y-2">
                    {filteredActivityLogs.map((log) => (
                      <div key={log.id} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1 flex-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">{log.action}</Badge>
                              <Badge variant="outline">{log.entity_type}</Badge>
                              <span className="text-sm text-muted-foreground">
                                {format(new Date(log.created_at), 'PPp', { locale: fr })}
                              </span>
                            </div>
                            <p className="text-sm font-medium">{log.entity_name}</p>
                            {log.user && (
                              <p className="text-sm text-muted-foreground">
                                Par {log.user.full_name}
                              </p>
                            )}
                            {log.details && (
                              <details className="text-sm text-muted-foreground">
                                <summary className="cursor-pointer hover:text-foreground">Détails</summary>
                                <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                                  {JSON.stringify(log.details, null, 2)}
                                </pre>
                              </details>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Aucune activité trouvée
                  </p>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};
